import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BellIcon, ClockIcon, PencilIcon, TrashIcon, UsersIcon } from "lucide-react";

import type { Goal } from "../types";
import { notifySummary } from "../utils";

interface GoalCardProps {
  readonly circleNameById: Map<string, string>;
  readonly goal: Goal;
  readonly onArchive: (goal: Goal) => void;
  readonly onEdit: (goal: Goal) => void;
}

export function GoalCard({ circleNameById, goal, onArchive, onEdit }: GoalCardProps) {
  return (
    <Card>
      <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
        <span className="font-medium">{goal.name}</span>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="text-xs tabular-nums">
            {goal.slots.length} slot{goal.slots.length !== 1 ? "s" : ""}
          </Badge>
          <button
            onClick={() => onEdit(goal)}
            aria-label={`Edit ${goal.name}`}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onArchive(goal)}
            aria-label={`Archive ${goal.name}`}
            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {goal.slots.map((slot) => (
          <div key={slot.id} className="flex items-center gap-3 px-4 py-2 border-t border-border/50 text-sm">
            <ClockIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="flex-1 text-foreground">
              {slot.name}
              {slot.recoveryAction && (
                <span className="block text-xs text-muted-foreground truncate">
                  if missed: {slot.recoveryAction}
                </span>
              )}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">{slot.dueTimeLocal}</span>
          </div>
        ))}
        {(goal.notifyCircleOnMiss && goal.notifyCircleId) || goal.sharedCircleIds.length > 0 ? (
          <div className="px-4 py-2.5 border-t border-border/50 space-y-1.5">
            {goal.notifyCircleOnMiss && goal.notifyCircleId && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <BellIcon className="w-3.5 h-3.5 text-primary shrink-0" />
                {notifySummary(goal, circleNameById.get(goal.notifyCircleId) ?? "Your circle")}
              </p>
            )}
            {goal.sharedCircleIds.length > 0 && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <UsersIcon className="w-3.5 h-3.5 text-primary shrink-0" />
                Progress shared with{" "}
                {goal.sharedCircleIds.map((id) => circleNameById.get(id) ?? "a circle").join(", ")}
              </p>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
